import { useEffect, useState } from "react";
import { api, API_BASE, COMPANY_LOGO } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Type, Check, Palette, Sparkles, KeyRound, Eye, EyeOff, LinkIcon, Copy, RefreshCw, UserCog } from "lucide-react";
import { NUM_FONTS, getNumFont, applyNumFont } from "@/lib/numFont";
import { PALETTES, getBrand, applyBrand, getCustomPalette, extractLogoPalette } from "@/lib/theme";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
  const [c, setC] = useState(null);
  const [font, setFont] = useState(getNumFont());
  const [brand, setBrand] = useState(getBrand());
  const [customPalette, setCustomPalette] = useState(getCustomPalette());
  const [extracting, setExtracting] = useState(false);
  useEffect(() => { (async () => setC((await api.get("/company")).data))(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/company", c);
      toast.success("Şirket bilgileri kaydedildi");
    } catch { toast.error("Kayıt başarısız"); }
  };

  const pickFont = (id) => {
    applyNumFont(id);
    setFont(id);
    toast.success("Rakam yazı tipi güncellendi");
  };

  const pickBrand = (id, custom) => {
    applyBrand(id, custom);
    setBrand(id);
    if (custom) setCustomPalette(custom);
    toast.success("Tema rengi uygulandı");
  };

  const generateFromLogo = async () => {
    const logo = c?.logo_url || COMPANY_LOGO;
    if (!logo) { toast.error("Önce logo yükleyin"); return; }
    setExtracting(true);
    try {
      const palette = await extractLogoPalette(logo);
      pickBrand("custom", palette);
      toast.success("Logo'dan palet oluşturuldu");
    } catch (e) {
      toast.error(e.message || "Palet oluşturulamadı");
    } finally {
      setExtracting(false);
    }
  };

  if (!c) return <div>Yükleniyor...</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Sistem</div>
        <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">Ayarlar</h1>
      </div>

      <div className="bg-white rounded-2xl border p-8">
        <div className="flex items-center gap-4 mb-6">
          <img src={c.logo_url || COMPANY_LOGO} alt="Logo" className="w-16 h-16 object-contain bg-slate-50 rounded-xl p-1 border" />
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Şirket Kimliği</div>
            <div className="font-display text-lg font-bold">{c.name}</div>
          </div>
        </div>

        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Ünvan</label>
            <input data-testid="co-name" value={c.name || ""} onChange={(e) => setC({...c, name: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
          <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Adres</label>
            <textarea data-testid="co-address" value={c.address || ""} onChange={(e) => setC({...c, address: e.target.value})} rows="2" className="w-full mt-1 px-3 py-2 border rounded-lg"></textarea></div>
          <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Vergi Dairesi</label>
            <input data-testid="co-tax-office" value={c.tax_office || ""} onChange={(e) => setC({...c, tax_office: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
          <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">VKN</label>
            <input data-testid="co-tax-number" value={c.tax_number || ""} onChange={(e) => setC({...c, tax_number: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
          <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Telefon</label>
            <input data-testid="co-phone" value={c.phone || ""} onChange={(e) => setC({...c, phone: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
          <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">E-posta</label>
            <input type="email" data-testid="co-email" value={c.email || ""} onChange={(e) => setC({...c, email: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
          <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Web Sitesi</label>
            <input data-testid="co-website" value={c.website || ""} onChange={(e) => setC({...c, website: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
          <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Logo URL</label>
            <input data-testid="co-logo" value={c.logo_url || ""} onChange={(e) => setC({...c, logo_url: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg text-xs font-mono-num" /></div>
          <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">IBAN (QR Ödeme Kodu için)</label>
            <input data-testid="co-iban" value={c.iban || ""} onChange={(e) => setC({...c, iban: e.target.value})} placeholder="TR__ ____ ____ ____ ____ ____ __" className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num text-sm" />
            <p className="text-xs text-slate-400 mt-1">Proforma PDF'lerine QR kod ile birlikte otomatik eklenir.</p>
          </div>
          <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Banka Bilgileri (Proforma alt bilgi)</label>
            <textarea data-testid="co-bank" value={c.bank_info || ""} onChange={(e) => setC({...c, bank_info: e.target.value})} rows="3" className="w-full mt-1 px-3 py-2 border rounded-lg"></textarea></div>
          <button data-testid="co-save" className="col-span-2 mt-3 py-3 bg-[#1E2A9C] text-white rounded-lg font-bold">Kaydet</button>
        </form>
      </div>

      {/* Brand palette picker */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl grid place-items-center text-white shadow-lg"
               style={{ background: `linear-gradient(135deg, var(--brand-primary), var(--brand-accent))` }}>
            <Palette className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Görsel</div>
            <h2 className="font-display text-2xl font-extrabold text-slate-900 dark:text-slate-100">Tema Renkleri</h2>
            <p className="text-sm text-slate-500 mt-1">Vurgu rengi paletini seçin — tüm butonlar, linkler, KPI'lar ve gradient'ler anında değişir.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="brand-picker">
          {PALETTES.map(p => {
            const active = brand === p.id;
            return (
              <button key={p.id} type="button" data-testid={`brand-${p.id}`}
                onClick={() => pickBrand(p.id)}
                className={`relative p-5 rounded-xl border-2 text-left transition overflow-hidden ${
                  active ? "border-slate-900 dark:border-white ring-2 ring-offset-2 dark:ring-offset-slate-900 ring-slate-900/20 dark:ring-white/20"
                         : "border-slate-200 dark:border-slate-700 hover:border-slate-400"
                }`}>
                {active && (
                  <span className="absolute top-3 right-3 w-6 h-6 rounded-full grid place-items-center text-white z-10"
                        style={{ backgroundColor: p.primary }}>
                    <Check className="w-3.5 h-3.5" />
                  </span>
                )}
                {/* Color swatch strip */}
                <div className="flex gap-1 mb-3">
                  <div className="h-14 flex-1 rounded" style={{ backgroundColor: p.primary }} />
                  <div className="h-14 flex-1 rounded" style={{ backgroundColor: p.primaryHover }} />
                  <div className="h-14 flex-1 rounded" style={{ backgroundColor: p.accent }} />
                  <div className="h-14 flex-1 rounded border border-slate-200" style={{ backgroundColor: p.tint }} />
                </div>
                <div className="font-bold text-sm text-slate-900 dark:text-slate-100">{p.label}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{p.tagline}</div>
                {/* Mini preview button */}
                <div className="mt-3 flex gap-2 items-center">
                  <span className="px-3 py-1.5 rounded-lg text-[10px] font-bold text-white"
                        style={{ backgroundColor: p.primary }}>Buton</span>
                  <span className="text-[10px] font-bold" style={{ color: p.primary }}>Link →</span>
                </div>
              </button>
            );
          })}
          {/* Custom palette (from logo) */}
          {customPalette && (
            <button type="button" data-testid="brand-custom"
              onClick={() => pickBrand("custom", customPalette)}
              className={`relative p-5 rounded-xl border-2 text-left transition overflow-hidden ${
                brand === "custom" ? "border-slate-900 dark:border-white ring-2 ring-offset-2 dark:ring-offset-slate-900 ring-slate-900/20 dark:ring-white/20"
                       : "border-slate-200 dark:border-slate-700 hover:border-slate-400"
              }`}>
              {brand === "custom" && (
                <span className="absolute top-3 right-3 w-6 h-6 rounded-full grid place-items-center text-white z-10"
                      style={{ backgroundColor: customPalette.primary }}>
                  <Check className="w-3.5 h-3.5" />
                </span>
              )}
              <div className="flex gap-1 mb-3">
                <div className="h-14 flex-1 rounded" style={{ backgroundColor: customPalette.primary }} />
                <div className="h-14 flex-1 rounded" style={{ backgroundColor: customPalette.primaryHover }} />
                <div className="h-14 flex-1 rounded" style={{ backgroundColor: customPalette.accent }} />
                <div className="h-14 flex-1 rounded border border-slate-200" style={{ backgroundColor: customPalette.tint }} />
              </div>
              <div className="font-bold text-sm text-slate-900 dark:text-slate-100 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-amber-500" /> {customPalette.label}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">{customPalette.tagline}</div>
              <div className="mt-3 flex gap-2 items-center">
                <span className="px-3 py-1.5 rounded-lg text-[10px] font-bold text-white" style={{ backgroundColor: customPalette.primary }}>Buton</span>
                <span className="text-[10px] font-bold" style={{ color: customPalette.primary }}>Link →</span>
              </div>
            </button>
          )}
        </div>

        <button type="button" data-testid="brand-from-logo" onClick={generateFromLogo} disabled={extracting}
          className="mt-4 flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-white transition disabled:opacity-50"
          style={{ background: `linear-gradient(135deg, var(--brand-primary), var(--brand-accent))` }}>
          <Sparkles className="w-4 h-4" /> {extracting ? "Analiz ediliyor..." : "Logodan Otomatik Palet Üret"}
        </button>

        <div className="mt-5 p-4 rounded-lg text-xs"
             style={{ backgroundColor: "var(--brand-tint)", color: "var(--brand-primary)", border: "1px solid var(--brand-accent)" }}>
          🎨 <b>Canlı Önizleme:</b> Bu kutu şu anki tema rengini gösterir. Palette değiştirin — anında değişir!
        </div>
      </div>

      {/* Font Picker for Dashboard Numbers */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 grid place-items-center text-white">
            <Type className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Görsel</div>
            <h2 className="font-display text-2xl font-extrabold text-slate-900 dark:text-slate-100">Rakam Yazı Tipi</h2>
            <p className="text-sm text-slate-500 mt-1">Panel'deki tutarlar, KPI'lar, tarihler için kullanılacak font. Seçim tarayıcınıza kaydedilir.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="font-picker">
          {NUM_FONTS.map(f => {
            const active = font === f.id;
            return (
              <button key={f.id} type="button" data-testid={`font-${f.id}`}
                onClick={() => pickFont(f.id)}
                className={`relative p-5 rounded-xl border-2 text-left transition ${
                  active
                    ? "border-[#1E2A9C] bg-[#1E2A9C]/5 dark:bg-[#1E2A9C]/20 ring-2 ring-[#1E2A9C]/20"
                    : "border-slate-200 dark:border-slate-700 hover:border-slate-400"
                }`}>
                {active && (
                  <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#1E2A9C] text-white grid place-items-center">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                )}
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-sm font-bold text-slate-900 dark:text-slate-100">{f.label}</span>
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{f.id}</span>
                </div>
                <div className="text-[10px] text-slate-500 mb-3">{f.tagline}</div>
                <div style={{ fontFamily: f.family, fontWeight: f.weight, letterSpacing: f.letter, fontVariantNumeric: "tabular-nums" }}
                     className="space-y-0.5 text-slate-900 dark:text-slate-100">
                  <div className="text-3xl">₺1.234.567,89</div>
                  <div className="text-xl text-slate-600 dark:text-slate-400">$45.678,00 · €12.345,67</div>
                  <div className="text-sm text-slate-500">0123456789</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-5 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
          💡 <b>İpucu:</b> Seçim tüm sayfalara anında uygulanır (Panel, KPI kartları, Ajanda saatleri, tablolar). Değişikliği görmek için Panel'e gidin.
        </div>
      </div>

      {/* Turkish PDF Font Preview */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-8" data-testid="pdf-font-preview">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 grid place-items-center text-white">
            <Type className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">PDF Çıktı</div>
            <h2 className="font-display text-2xl font-extrabold text-slate-900 dark:text-slate-100">Türkçe Font Ön İzleme</h2>
            <p className="text-sm text-slate-500 mt-1">Proforma, ekstre, ajanda PDF'lerinde Türkçe karakterler ve ₺ nasıl görünecek?</p>
          </div>
        </div>

        <div className="p-6 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950"
             style={{ fontFamily: "'DejaVu Sans', 'Liberation Sans', 'Arial', sans-serif" }}>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Şirket Bilgileri</div>
              <div className="text-slate-900 dark:text-slate-100"><b>MADYMARK İÇ VE DIŞ TİCARET SANAYİ A.Ş</b></div>
              <div className="text-sm text-slate-700 dark:text-slate-300 mt-1">TAHTAKALE FIRAT 1 CADDE 6/9 NO 88 AVCILAR/İSTANBUL</div>
              <div className="text-sm text-slate-700 dark:text-slate-300">BAŞAKŞEHİR VERGİ DAİRESİ - VKN: 6101146549</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Türkçe Karakter Seti</div>
              <div className="text-2xl text-slate-900 dark:text-slate-100 tracking-wide">ç ğ ı ö ş ü Ç Ğ İ Ö Ş Ü</div>
              <div className="text-sm text-slate-700 dark:text-slate-300 mt-2">Öğün, İğne, Şeker, Ürün, Çorap, Ödeme</div>
            </div>
          </div>
          <div className="mt-6 border-t border-slate-200 dark:border-slate-700 pt-4 grid grid-cols-3 gap-4 text-slate-900 dark:text-slate-100">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Ara Toplam</div>
              <div className="text-lg font-bold mt-1">₺100,00</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">KDV Toplam</div>
              <div className="text-lg font-bold mt-1">₺20,00</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Genel Toplam</div>
              <div className="text-xl font-extrabold mt-1" style={{ color: "var(--brand-primary)" }}>₺120,00 · $45,00 · €12,50</div>
            </div>
          </div>
          <div className="mt-4 text-xs text-slate-500 italic">
            Örnek: "Öğün molası müşteriye ödenecektir. İğne, Şeker, Türkçe Ürün çğüöşı."
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-800 dark:text-emerald-300">
          <Check className="w-4 h-4 flex-shrink-0" />
          <span><b>DejaVu Sans</b> yüklü — tüm Türkçe karakterler ve ₺ (Lira) sembolü PDF'lerde doğru render ediliyor</span>
        </div>
      </div>

      <ChangePasswordCard />
      <ResetRequestsCard />
    </div>
  );
}

function ChangePasswordCard() {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) return toast.error("Yeni şifre en az 8 karakter olmalı");
    if (nw !== nw2) return toast.error("Yeni şifreler eşleşmiyor");
    if (nw === cur) return toast.error("Yeni şifre mevcut şifreyle aynı olamaz");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      if (data.token) localStorage.setItem("madymark_token", data.token);
      toast.success("Şifreniz güncellendi");
      setCur(""); setNw(""); setNw2("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Şifre değiştirilemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800" data-testid="change-pw-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#1E2A9C]/10 grid place-items-center">
          <KeyRound className="w-5 h-5 text-[#1E2A9C]" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Güvenlik</div>
          <div className="font-display text-xl font-extrabold text-slate-900 dark:text-slate-100">Şifre Değiştir</div>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Hesap: <span className="font-mono-num font-bold">{user?.email}</span> · Şifreniz en az 8 karakter olmalıdır.
      </p>
      <form onSubmit={submit} className="space-y-3 max-w-md">
        <PwField label="Mevcut Şifre" value={cur} onChange={setCur} type={show ? "text" : "password"} testId="pw-cur"
                 onToggle={() => setShow(!show)} show={show} />
        <PwField label="Yeni Şifre" value={nw} onChange={setNw} type={show ? "text" : "password"} testId="pw-new" />
        <PwField label="Yeni Şifre (tekrar)" value={nw2} onChange={setNw2} type={show ? "text" : "password"} testId="pw-new2" />
        <button type="submit" disabled={busy} data-testid="pw-submit"
          className="px-6 py-2.5 rounded-lg bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] disabled:opacity-60 transition">
          {busy ? "Kaydediliyor..." : "Şifreyi Değiştir"}
        </button>
      </form>
    </div>
  );
}

function PwField({ label, value, onChange, type, testId, onToggle, show }) {
  return (
    <div>
      <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">{label}</label>
      <div className="relative">
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
          required autoComplete="new-password"
          className="w-full mt-1 px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-[#1E2A9C]/30 outline-none" />
        {onToggle && (
          <button type="button" onClick={onToggle} data-testid="pw-toggle"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}

function ResetRequestsCard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [pickEmail, setPickEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([
        api.get("/auth/reset-requests"),
        api.get("/auth/users"),
      ]);
      setItems(r.data); setUsers(u.data); setDenied(false);
    } catch (err) {
      if (err.response?.status === 403) { setDenied(true); }
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (user?.role === "owner") load();
    else setDenied(true);
    // eslint-disable-next-line
  }, [user?.role]);

  // SEC-001: only show card to owners
  if (denied || user?.role !== "owner") return null;

  const buildLink = (token) => `${window.location.origin}/sifre-sifirla?token=${token}`;
  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(buildLink(token));
      toast.success("Bağlantı panoya kopyalandı");
    } catch { toast.error("Kopyalanamadı — bağlantıyı elle seçip kopyalayın"); }
  };

  const genLink = async () => {
    if (!pickEmail) return toast.error("Kullanıcı seçin");
    try {
      const { data } = await api.post("/auth/admin-reset-link", { email: pickEmail });
      toast.success("Sıfırlama bağlantısı oluşturuldu");
      await copyLink(data.token);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bağlantı oluşturulamadı");
    }
  };

  const statusBadge = (s) => {
    const map = {
      active:      ["Aktif",       "bg-emerald-100 text-emerald-700"],
      used:        ["Kullanıldı",  "bg-slate-200 text-slate-600"],
      expired:     ["Süresi Dolmuş","bg-amber-100 text-amber-700"],
      invalidated: ["İptal",        "bg-rose-100 text-rose-700"],
    };
    const [t, cls] = map[s] || [s, "bg-slate-100 text-slate-600"];
    return <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${cls}`}>{t}</span>;
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800" data-testid="reset-requests-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 grid place-items-center">
            <UserCog className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Yönetim</div>
            <div className="font-display text-xl font-extrabold text-slate-900 dark:text-slate-100">Şifre Sıfırlama Bağlantıları</div>
          </div>
        </div>
        <button onClick={load} data-testid="rr-refresh"
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-bold hover:border-[#1E2A9C]">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Yenile
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Herhangi bir kullanıcı için 24 saat geçerli bir bağlantı üretin, kopyalayıp kişiye gönderin.
        Kullanıcının "Şifremi Unuttum" talebi varsa aşağıdaki tabloda görürsünüz.
      </p>

      <div className="flex items-end gap-2 mb-5 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
        <div className="flex-1">
          <label className="text-[10px] uppercase font-bold text-amber-700 tracking-widest">Kullanıcı Seç</label>
          <select value={pickEmail} onChange={(e) => setPickEmail(e.target.value)}
            data-testid="rr-pick"
            className="w-full mt-1 px-3 py-2 border border-amber-300 rounded-lg bg-white dark:bg-slate-800 text-sm">
            <option value="">— Kullanıcı seçin —</option>
            {users.map(u => <option key={u.email} value={u.email}>{u.name} · {u.email}</option>)}
          </select>
        </div>
        <button onClick={genLink} disabled={!pickEmail} data-testid="rr-generate"
          className="flex items-center gap-1 px-4 py-2 rounded-lg bg-amber-600 text-white font-bold text-sm hover:bg-amber-700 disabled:opacity-40">
          <LinkIcon className="w-3.5 h-3.5" /> Bağlantı Oluştur
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-6 text-xs text-slate-400">Kayıt yok</div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto space-y-2" data-testid="rr-list">
          {items.map(it => (
            <div key={it.id} className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg flex items-center gap-3 group">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-slate-800 dark:text-slate-200">{it.user_name || it.user_email.split("@")[0]}</span>
                  <span className="text-xs text-slate-500 font-mono-num">{it.user_email}</span>
                  {statusBadge(it.status)}
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 font-bold uppercase">
                    {it.source === "admin" ? "Admin" : "Kullanıcı Talebi"}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1 font-mono-num flex gap-3 flex-wrap">
                  <span>Oluşturma: {fmt(it.created_at)}</span>
                  <span>Bitiş: {fmt(it.expires_at)}</span>
                  {it.used_at && <span className="text-emerald-600">Kullanıldı: {fmt(it.used_at)}</span>}
                </div>
              </div>
              {it.status === "active" && (
                <button onClick={() => copyLink(it.token)}
                  data-testid={`rr-copy-${it.id}`}
                  className="opacity-70 group-hover:opacity-100 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1E2A9C] text-white font-bold text-xs hover:bg-[#121A68] transition">
                  <Copy className="w-3 h-3" /> Bağlantıyı Kopyala
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

